import React, { ReactElement, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'

import {
  fetchEvents,
  fetchSessions,
  fetchUserGroups,
  fetchUsers,
  selectEventById,
  selectEventsStatus,
  selectInvites,
  selectSessionsByID,
  selectSessionsStatus,
  selectUserGroupsByLinkedService,
  selectUserGroupsStatusByLinkedService,
  selectUsersByLinkedService,
  selectUsersStatusByLinkedService,
} from './events.slice'

export default ({
  sessionID,
  eventID,
  checkedUsers,
  setCheckedUsers,
  adminOnly,
}: {
  sessionID: string
  eventID?: string
  checkedUsers: string[]
  setCheckedUsers: React.Dispatch<React.SetStateAction<string[]>>
  adminOnly?: boolean
}): ReactElement => {
  let dispatch = useAppDispatch()

  let session = useAppSelector(selectSessionsByID(sessionID))
  let sessionsStatus = useAppSelector(selectSessionsStatus)
  let event = useAppSelector(selectEventById(eventID || session?.eventID || ''))
  let lsid = event?.linkedServiceID || ''
  let eventsStatus = useAppSelector(selectEventsStatus)
  let users = useAppSelector(selectUsersByLinkedService(lsid))
  let usersStatus = useAppSelector(selectUsersStatusByLinkedService(lsid))
  let userGroups = useAppSelector(selectUserGroupsByLinkedService(lsid))
  let userGroupsStatus = useAppSelector(
    selectUserGroupsStatusByLinkedService(lsid),
  )
  let invites = useAppSelector(selectInvites)

  let [userFilter, setUserFilter] = useState<string>('')
  let [groupFilter, setGroupFilter] = useState<string>('')
  let [userPage, setUserPage] = useState<number>(1)
  let [adminUserID, setAdminUserID] = useState<string>('')
  let [adminError, setAdminError] = useState<string>('')

  let usersPerPage = 10

  let invitedUsers = invites[sessionID]?.map((inv) => inv.userID) || []
  let filterGroup = userGroups?.find((ug) => ug.id === groupFilter)
  let groupChecked = filterGroup?.users?.every((u) => checkedUsers.includes(u))

  let filteredUsers =
    // eslint-disable-next-line fp/no-mutating-methods
    users
      ?.filter(
        (u) =>
          (`${u.firstName} ${u.lastName}`
            .toLowerCase()
            .includes(userFilter.toLowerCase()) ||
            u.email.toLowerCase().includes(userFilter.toLowerCase())) &&
          (!groupFilter || filterGroup?.users?.some((gu) => u.id === gu)),
      )
      .sort(
        (a, b) =>
          Number(invitedUsers.includes(b.id)) -
            Number(invitedUsers.includes(a.id)) ||
          a.firstName.localeCompare(b.firstName),
      ) || []

  useEffect(() => {
    if (eventsStatus === 'uninitialized') {
      dispatch(fetchEvents())
    }
  }, [eventsStatus])

  useEffect(() => {
    if (sessionsStatus === 'uninitialized') {
      dispatch(fetchSessions())
    }
  }, [sessionsStatus])

  useEffect(() => {
    if (lsid && (!usersStatus || usersStatus === 'uninitialized')) {
      dispatch(fetchUsers(lsid))
    }
  }, [usersStatus, lsid])

  useEffect(() => {
    if (lsid && (!userGroupsStatus || userGroupsStatus === 'uninitialized')) {
      dispatch(fetchUserGroups(lsid))
    }
  }, [userGroupsStatus, lsid])

  return (
    <Box>
      {!adminOnly ? (
        <>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Invite Users
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <TextField
              sx={{ bgcolor: 'background.paperDark', flex: '1 1 200px' }}
              size="small"
              variant="outlined"
              value={userFilter}
              label="Search Users"
              onChange={(e) => {
                setUserPage(1)
                setUserFilter(e.target.value)
              }}
            />
            <FormControl fullWidth size="small" sx={{ flex: '1 1 200px' }}>
              <InputLabel id="group-label">User Group</InputLabel>
              <Select
                sx={{ bgcolor: 'background.paperDark' }}
                value={groupFilter}
                label="User Group"
                labelId="group-label"
                onChange={(e) => {
                  setUserPage(1)
                  setGroupFilter(e.target.value)
                }}
              >
                <MenuItem value="">Unselected</MenuItem>
                {userGroups?.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {filterGroup && !userFilter && (
              <Card
                sx={{
                  py: 0.5,
                  px: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  bgcolor: 'background.paperDark',
                }}
              >
                <Checkbox
                  sx={{ ml: -1 }}
                  checked={groupChecked}
                  onChange={() =>
                    setCheckedUsers(groupChecked ? [] : filterGroup.users || [])
                  }
                />
                <Typography>Select all users in {filterGroup.name}</Typography>
              </Card>
            )}
            {filteredUsers
              ?.slice((userPage - 1) * usersPerPage, userPage * usersPerPage)
              ?.map((u) => (
                <Card
                  key={u.id}
                  sx={{
                    py: 0.5,
                    px: 1.5,
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <Checkbox
                    sx={{ ml: -1 }}
                    checked={checkedUsers.includes(u.id)}
                    onChange={() =>
                      setCheckedUsers(
                        checkedUsers.includes(u.id)
                          ? checkedUsers.filter((cu) => cu !== u.id)
                          : [...checkedUsers, u.id],
                      )
                    }
                  />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography>
                      {u.firstName} {u.lastName}
                    </Typography>
                    <Typography variant="subtitle2" color="textSecondary">
                      {u.email}
                    </Typography>
                  </Box>
                  {invites[sessionID]?.some((inv) => inv.userID === u.id) && (
                    <Chip size="small" label="Invited" />
                  )}
                </Card>
              ))}
          </Box>
          {!filteredUsers.length && (
            <Typography>No users match this search.</Typography>
          )}
          <Pagination
            sx={{ mt: 0.5, display: 'flex', justifyContent: 'center' }}
            page={userPage}
            onChange={(_, value) => setUserPage(value)}
            count={Math.ceil(filteredUsers.length / usersPerPage)}
            variant="outlined"
            size="small"
          />
        </>
      ) : (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Admin: Direct User ID Invite
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              sx={{ bgcolor: 'background.paperDark', flexGrow: 1 }}
              size="small"
              variant="outlined"
              placeholder="Enter User ID"
              value={adminUserID}
              onChange={(e) => {
                setAdminUserID(e.target.value)
                setAdminError('')
              }}
              error={!!adminError}
              helperText={adminError}
            />
            <Button
              variant="contained"
              onClick={() => {
                if (!adminUserID.trim()) {
                  setAdminError('User ID is required')
                  return
                }
                if (checkedUsers.includes(adminUserID)) {
                  setAdminError('User already added')
                  return
                }
                setCheckedUsers([...checkedUsers, adminUserID])
                setAdminUserID('')
                setAdminError('')
              }}
            >
              Invite
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}
